import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { TurnSlots } from "../thread/slots";

/** `prose`, in the terminal register — wrapped text with inline code lifted
 * out, and nothing else.
 *
 * The native skin renders full GFM through `std-prosemirror`, which is right
 * there and wrong here: a rich editor brings proportional type, block
 * margins and a document model, and a monospace column has none of those. But
 * dropping markdown entirely would be worse than either, because the measured
 * fact behind this slot is that **57.2% of coalesced assistant messages carry
 * inline code** — the backticked path is the single most common thing a
 * reader is trying to pick out of a paragraph.
 *
 * So this renders exactly that one construct and leaves the rest as text.
 * The choice is not "less markdown because it was cheaper"; it is that
 * emphasis in a monospace register is brightness, and inline code is the one
 * construct whose meaning survives being drawn that way. Headings, bold and
 * bullets all lower to "some other text that is also monospace", which is
 * indistinguishable from not rendering them.
 *
 * Fenced blocks are deliberately NOT handled: a fence is a `term-block`, and
 * the composition is what decides that a message contains one. Splitting a
 * fence out here would put a layout decision in a leaf. */
@component({ name: "std-term-turn-prose" })
export class TermTurnProse extends LonaWebComponent {
  private $text: HTMLElement = this.$("text");

  static makeWith(props: TurnSlots.Props["prose"]): TermTurnProse {
    return (TermTurnProse.make() as TermTurnProse).bind(props);
  }

  bind(props: TurnSlots.Props["prose"]): this {
    this.$text.replaceChildren(...TermTurnProse.segments(props.text));
    return this;
  }

  /** Split on paired single backticks. An unpaired backtick is literal text —
   * the trailing segment is appended verbatim, so a lone tick never eats the
   * rest of a message. */
  static segments(text: string): Node[] {
    const nodes: Node[] = [];
    const pattern = /`([^`\n]+)`/g;
    let last = 0;
    for (const match of text.matchAll(pattern)) {
      const at = match.index;
      if (at > last) nodes.push(document.createTextNode(text.slice(last, at)));
      const $code = document.createElement("span");
      $code.className = "code";
      $code.textContent = match[1];
      nodes.push($code);
      last = at + match[0].length;
    }
    if (last < text.length) nodes.push(document.createTextNode(text.slice(last)));
    return nodes;
  }

  static $styles = [
    css`
      :host {
        display: block;
        font-family: var(--font-mono, ui-monospace, monospace);
        font-size: var(--turn-term-size, 12px);
        line-height: var(--turn-term-leading, 1.7);
        color: var(--text-pri, #1f2328);
      }
      #text {
        margin: 0;
        white-space: pre-wrap;
        overflow-wrap: anywhere;
      }
      /* Brightness is the whole emphasis vocabulary here: no box, no tint,
         no second family — those would all read as a different KIND of row
         rather than as a word inside this one. */
      .code {
        color: var(--fg-blue, #3b82f6);
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`<p id="text"></p>`;
}
